import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useHistory } from 'react-router-dom';
import cx from 'classnames';
import styles from '@/pages/task-list/styles.less';
import { useQuery } from '@/common/hooks/index';
import { routes } from '@/app/routing/index';
import {
  TASK_MODULES,
  getTaskModuleLabelKey,
} from '@/common/constants/constants';
import {
  AssetTasks,
  ImmediateTasks,
  MainTasks,
} from '@/common/containers/Forms/index';
import LegalTasks from '@/common/containers/Forms/LegalTasks';
import {
  useCurrentDecedentId,
  useCurrentDecedentSections,
} from '@/app/providers/DecedentProvider';
import { useSubmitQuestionMutation } from '@/entities/decedent';
import Button from '@mui/material/Button';
import { PromptSaveModal } from '@/common/containers/Modals/index';
import { Trans, useTranslation } from 'react-i18next';

const FORM_ID = 'editQuestionnaire';

const BACK_LINK_DATA = (module, t) => ({
  module: {
    text: t('task list'),
    link: `${routes.dashboard.taskList}/${module}`,
  },
  entryLists: {
    text: t('entry lists'),
    link: routes.dashboard.entryLists,
  },
  institutions: {
    text: t('financial Institutions'),
    link: routes.dashboard.finInstitutions,
  },
});

const EditQuestions = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({});
  const submitRef = useRef(() => {});
  const history = useHistory();
  const decedentId = useCurrentDecedentId();
  const sections = useCurrentDecedentSections();
  const submitQuestionMutation = useSubmitQuestionMutation();
  const loading = submitQuestionMutation.isPending;

  const query = useQuery();
  const module = query.get('module') || '';
  const moduleLabel = t(getTaskModuleLabelKey(module));
  const submitQuestion = useCallback(
    (payload) => submitQuestionMutation.mutateAsync(payload),
    [submitQuestionMutation]
  );

  useEffect(() => {
    window.onbeforeunload = formState.isDirty ? () => true : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [formState.isDirty, loading]);

  const onSubmit = useCallback(
    async (form) => {
      await submitQuestion({
        decedentId,
        answers: form,
        section: module,
      });

      if (module === TASK_MODULES.LEGAL && !sections?.legal?.question?.passed) {
        history.push(`${routes.dashboard.taskList}/legal`);
      }
    },
    [decedentId, history, module, sections, submitQuestion]
  );

  const Form = useMemo(() => {
    const props = {
      onSubmit,
      formId: FORM_ID,
      onFormStateChange: (form, submitForm) => {
        setFormState(form);
        submitRef.current = submitForm;
      },
      validationMode: 'onChange',
    };
    const forms = {
      [TASK_MODULES.IMMEDIATE]: ImmediateTasks,
      [TASK_MODULES.MAIN]: MainTasks,
      [TASK_MODULES.ASSET]: AssetTasks,
      [TASK_MODULES.LEGAL]: LegalTasks,
    };
    const Component = forms[module];
    return Component ? React.cloneElement(<Component />, props) : null;
  }, [module, onSubmit]);

  const saveProgress = useCallback(async () => {
    if (!formState.isValid) {
      submitRef.current();
      throw new Error(t('form not valid'));
    }
    await submitRef.current();
  }, [formState.isValid, t]);

  const BackLink = useMemo(() => {
    const backPage = query.get('backPage');

    const data = BACK_LINK_DATA(module, t)[backPage || 'module'];
    return (
      <Link className={styles.editPageBack} to={data.link}>
        <ArrowBackIcon /> {t('Back to {{text}}', { text: data.text })}
      </Link>
    );
  }, [module, query, t]);

  const noteByModule = useMemo(
    () => ({
      [TASK_MODULES.IMMEDIATE]: (
        <Trans
          i18nKey="Any changes to the answers you have previously provided below may affect the current <1>Immediate tasks</1> list."
          components={{ 1: <b /> }}
        />
      ),
      [TASK_MODULES.LEGAL]: (
        <>
          {t(
            'Please answer the following questions or provide the information as requested.'
          )}
        </>
      ),
      [TASK_MODULES.MAIN]: (
        <Trans
          i18nKey="Any changes to the answers you have previously provided below may affect the current <1>Main tasks</1> list."
          components={{ 1: <b /> }}
        />
      ),
      [TASK_MODULES.ASSET]: (
        <Trans
          i18nKey="Any changes to the answers you have previously provided below may affect the current <1>Assets tasks</1> list."
          components={{ 1: <b /> }}
        />
      ),
      default: <>{t('Updates in answers can affect the list of tasks')}</>,
    }),
    [t]
  );

  return (
    <section className={cx(styles.page, styles.editPage)}>
      <PromptSaveModal
        when={!!formState.isDirty && !loading}
        onSave={saveProgress}
      />
      {BackLink}
      <h3 className={styles.editPageTitle}>
        {module === TASK_MODULES.LEGAL
          ? t('Legal tasks questionnaire')
          : t('Edit {{module}} questionnaire', { module: moduleLabel })}
      </h3>
      <div className={styles.editPageWrapper}>
        <div className={styles.editPageForm}>
          <div className={cx(styles.editPageFormNote, 'note')}>
            {noteByModule[module] || noteByModule.default}
          </div>
          {Form}
        </div>
      </div>
      <div className={styles.editPageBtnSubmit}>
        <Button
          variant="contained"
          type="submit"
          color="success"
          form={FORM_ID}
          disabled={loading || !formState.isDirty}
          loading={loading}
        >
          {module !== TASK_MODULES.LEGAL
            ? t('Save Updates')
            : !sections?.legal?.question?.passed
            ? t('Finish questionnaire')
            : t('Save Updates')}
        </Button>
      </div>
    </section>
  );
};

export default EditQuestions;
